import { mergeOptions } from '../../../libs/gin-tonic/helpers/common/merge-options';
import { newGuid } from '../../../libs/gin-tonic/helpers/common/new-guid';
import { createNode } from '../../../libs/gin-tonic/helpers/dom/create-node';
import { ConnectedPositionStrategy, Overlay, OverlayOptions, OverlayPositionBuilder } from '../../../libs/gin-tonic/helpers/overlay';
import { RepositionScrollStrategy } from '../../../libs/gin-tonic/helpers/overlay/scroll/reposition-scroll-strategy';
import { stripDiacritics } from '../../../libs/gin-tonic/helpers/string/strip-diacritics/index';

export interface SelectOptions {
	componentClassName: string;
	multipleClassName: string;
	activeClassName: string;
	selectedClassName: string;
	disabledClassName: string;
	searchEnabled?: boolean;
	searchPlaceholder?: string;
	searchNoEntriesFound?: string;
	searchClearIconHtml?: string;
	searchFilter?: (value: string, safeValue: string) => HTMLOptionElement[];
	iconHtml?: string;
}

export class Select {
	private overlayRef: Overlay | undefined;
	private position: ConnectedPositionStrategy | undefined;
	private disabledObserver?: MutationObserver;
	private select: HTMLElement;
	private activeIndex: number;
	private searchElement?: HTMLElement;
	private optionElements: HTMLElement[] = [];
	private triggerTextElement: HTMLSpanElement;
	private options: SelectOptions;
	private multiple: boolean;
	private panelOpen = false;
	private searching = false;
	private searchFocused = false;

	static options: SelectOptions = {
		componentClassName: 'c-select',
		multipleClassName: 'is-multiple',
		activeClassName: 'is-active',
		selectedClassName: 'is-selected',
		disabledClassName: 'is-disabled'
	};

	get selectedIndex() {
		return this.element.selectedIndex;
	}

	get selectedOptions() {
		return Array.from(this.element.selectedOptions);
	}

	get value() {
		return this.selectedOptions.map(o => o.value).join(',');
	}

	constructor(private element: HTMLSelectElement, options?: Partial<SelectOptions>) {
		this.options = mergeOptions(Select, options, element);

		this.multiple = this.element.hasAttribute('multiple');

		this.activeIndex = this.selectedIndex;

		const tabIndex = this.element.tabIndex || 0;
		this.element.tabIndex = -1;
		this.element.setAttribute('aria-hidden', 'true');

		this.select = document.createElement('div');
		this.select.id = newGuid();
		this.select.role = 'combobox';
		this.element.dataset['select'] = this.select.id;
		this.select.classList.add(this.options.componentClassName);
		if (this.multiple) {
			this.select.classList.add(this.options.multipleClassName);
		}
		this.handleDisabled();
		this.select.tabIndex = tabIndex;
		this.element.classList.forEach(
			className =>
				// we should never copy js classes, because afterwise we have an infinite loop on js-select if we add observe: true
				className.indexOf('js-') === -1 && this.select.classList.add(className)
		);

		this.select.dataset['value'] = this.value;

		this.triggerTextElement = document.createElement('span');
		this.triggerTextElement.classList.add(`${this.options.componentClassName}__value`);
		this.setTriggerText();

		if (this.value) {
			this.select.classList.add(this.options.activeClassName);
		} else {
			this.select.classList.remove(this.options.activeClassName);
		}

		this.select.appendChild(this.triggerTextElement);

		const triggerArrow = this.options.iconHtml ? createNode(this.options.iconHtml) : document.createElement('span');
		triggerArrow.classList.add(`${this.options.componentClassName}__arrow`);
		this.select.appendChild(triggerArrow);

		this.element.insertAdjacentElement('afterend', this.select);
		this.element.addEventListener('focus', () => {
			if (this.element.disabled) {
				return;
			}

			this.select.focus();
			this.open();
		});

		this.select.addEventListener('click', event => {
			event.preventDefault();
			event.stopPropagation();
			this.toggle();
		});

		this.select.addEventListener('keydown', (event: KeyboardEvent) => this.handleKeydown(event));

		this.element.addEventListener('op10.change', () => {
			requestAnimationFrame(() => {
				this.activeIndex = this.element.selectedIndex;
				this.setSelectedValue(false);
			});
		});

		this.element.addEventListener('op10.reset', () => {
			requestAnimationFrame(() => {
				this.handleDisabled();
				this.overlayRef?.dispose();
				this.overlayRef = undefined;
			});
		});

		this.disabledObserver = new MutationObserver(mutations => {
			for (const mutation of mutations) {
				if (mutation.type === 'attributes') {
					this.handleDisabled();

					if (this.panelOpen && mutation.attributeName === 'disabled') {
						this.close();
					}
				}
			}
		});

		this.disabledObserver.observe(this.element, {
			attributes: true,
			attributeFilter: ['disabled']
		});
	}

	open() {
		if (!this.element.options.length || this.panelOpen || this.element.disabled) {
			return;
		}

		this.panelOpen = true;

		if (!this.overlayRef) {
			const panel = document.createElement('div');
			panel.classList.add(`${this.options.componentClassName}__panel`);

			panel.addEventListener('keydown', (event: KeyboardEvent) => this.handleKeydown(event));

			this.addSearch(panel);
			this.addOptions(panel, Array.from(this.element.options));

			this.position = OverlayPositionBuilder.connectedTo(this.select)
				.withFallbackPosition(
					{
						originX: 'start',
						originY: 'bottom'
					},
					{
						overlayX: 'start',
						overlayY: 'top'
					}
				)
				.withFallbackPosition(
					{
						originX: 'end',
						originY: 'bottom'
					},
					{
						overlayX: 'end',
						overlayY: 'top'
					}
				)
				.withFallbackPosition(
					{
						originX: 'start',
						originY: 'top'
					},
					{
						overlayX: 'start',
						overlayY: 'bottom'
					}
				)
				.withFallbackPosition(
					{
						originX: 'end',
						originY: 'top'
					},
					{
						overlayX: 'end',
						overlayY: 'bottom'
					}
				);

			this.overlayRef = new Overlay(
				panel,
				new OverlayOptions({
					scrollStrategy: new RepositionScrollStrategy(),
					positionStrategy: this.position,
					backdropClassName: 'c-select__overlay__backdrop',
					hasBackdrop: true
				})
			).create();

			this.overlayRef.backdropClick.subscribe(() => this.close());

			this.overlayRef.afterOpened.subscribe(() => {
				if (this.searchFocused) {
					return;
				}

				if (this.searchElement) {
					this.searchElement.querySelector('input')?.focus();
					this.searchFocused = true;
				}
			});

			this.overlayRef.afterClosed.subscribe(() => {
				this.overlayRef?.dispose();
				this.searchFocused = false;
			});

			this.overlayRef.onDetached.subscribe(() => (this.panelOpen = false));
		}

		this.overlayRef.open();
		this.setActiveValue(this.getOptionIndex(this.activeIndex));

		this.select.classList.add(this.options.activeClassName);
	}

	close() {
		if (!this.panelOpen) {
			return;
		}

		this.overlayRef?.close();
		this.element.dispatchEvent(new Event('blur'));

		if (!this.value) {
			this.select.classList.remove(this.options.activeClassName);
		}
	}

	toggle() {
		this.panelOpen ? this.close() : this.open();
	}

	private handleDisabled() {
		if (this.element.disabled) {
			this.select.classList.add(this.options.disabledClassName);
		} else {
			this.select.classList.remove(this.options.disabledClassName);
		}
	}

	private addOptions(panel: HTMLElement, options: HTMLOptionElement[]) {
		if (this.optionElements?.length) {
			for (const option of this.optionElements) {
				option.parentElement?.removeChild(option);
			}
		}

		this.optionElements = options.map(option => {
			const optionElement = document.createElement('div');
			optionElement.classList.add(`${this.options.componentClassName}__option`);
			if (option.classList.length > 0) {
				optionElement.classList.add(...option.classList.value.split(' '));
			}
			if (option.disabled) {
				optionElement.classList.add(this.options.disabledClassName);
			}
			optionElement.dataset['value'] = option.value;

			if (this.multiple) {
				const optionCheckbox = document.createElement('span');
				optionCheckbox.classList.add(`${this.options.componentClassName}__option-checkbox`);
				optionElement.appendChild(optionCheckbox);
			}

			const optionText = document.createElement('span');
			optionText.classList.add(`${this.options.componentClassName}__option-text`);
			optionText.textContent = option.text;

			optionElement.appendChild(optionText);

			return optionElement;
		});

		this.optionElements.forEach(optionElement => {
			panel.appendChild(optionElement);
		});

		// e.g. after search we need to correct the active index.
		if (this.optionElements.length) {
			this.activeIndex = this.getOptionIndex(this.activeIndex) !== -1 ? this.activeIndex : this.getNativeOptionIndex(0);
		}

		this.optionElements.forEach((option, index) => {
			option.addEventListener('click', () => {
				this.activeIndex = this.getNativeOptionIndex(index);
				const option = this.element.options[this.activeIndex];

				if (option.disabled) {
					return;
				}

				this.setSelectedValue(true);

				if (!this.multiple) {
					this.close();
				}
				this.select.focus();
			});
		});
	}

	private addSearch(panel: HTMLElement) {
		if (!this.options.searchEnabled) {
			return;
		}

		const search = document.createElement('div');
		const searchNoEntriesFound = document.createElement('div');
		const searchInputContainer = document.createElement('div');
		const searchInput = document.createElement('input');

		search.classList.add(`${this.options.componentClassName}-search`);
		searchNoEntriesFound.classList.add(`${this.options.componentClassName}-search-no-entries-found`, 'is-hidden');
		searchNoEntriesFound.textContent = this.options.searchNoEntriesFound || 'Keine Einträge gefunden';
		searchInputContainer.classList.add(`${this.options.componentClassName}-search-input-container`);
		searchInput.classList.add(`${this.options.componentClassName}-search-input`);
		searchInput.placeholder = this.options.searchPlaceholder || '';

		searchInputContainer.appendChild(searchInput);

		if (this.options.searchClearIconHtml) {
			const searchInputClear = document.createElement('button');

			searchInputClear.type = 'button';
			searchInputClear.classList.add('c-input__icon');
			searchInputClear.tabIndex = -1;
			searchInputClear.innerHTML = this.options.searchClearIconHtml;

			searchInputClear.addEventListener('click', () => {
				searchInput.value = '';
				searchInput.dispatchEvent(new InputEvent('input'));
			});

			searchInputContainer.appendChild(searchInputClear);
		}

		search.appendChild(searchInputContainer);
		panel.appendChild(search);
		panel.appendChild(searchNoEntriesFound);

		searchInput.addEventListener('focus', () => {
			this.searching = true;
		});
		searchInput.addEventListener('blur', () => {
			this.searching = false;
		});
		searchInput.addEventListener('input', () => {
			const value = stripDiacritics(searchInput.value.trim().toLowerCase());

			const options = !value
				? Array.from(this.element.options)
				: this.options.searchFilter
				? this.options.searchFilter(searchInput.value, value)
				: Array.from(this.element.options).filter(o => stripDiacritics(o.textContent?.trim())?.toLowerCase()?.includes(value));

			if (options.length) {
				searchNoEntriesFound.classList.add('is-hidden');
			} else {
				searchNoEntriesFound.classList.remove('is-hidden');
			}

			this.addOptions(panel, options);
			this.setActiveValue(this.getOptionIndex(this.activeIndex));
		});

		this.searchElement = search;
	}

	private handleKeydown(event: KeyboardEvent) {
		if (this.element.disabled) {
			return;
		}

		const delta = event.code === 'ArrowUp' || event.code === 'ArrowLeft' ? -1 : 1;
		let newIndex = this.getOptionIndex(this.activeIndex);

		switch (event.code) {
			case 'ArrowUp':
			case 'ArrowDown':
			case 'ArrowLeft':
			case 'ArrowRight':
				event.preventDefault();

				if (this.multiple && !this.panelOpen) {
					return;
				}

				if (newIndex + delta >= 0 && newIndex + delta < this.optionElements.length) {
					for (let i = 1; i <= this.optionElements.length; i++) {
						const index = (newIndex + delta * i + this.optionElements.length) % this.optionElements.length;

						if (!this.element.options[this.getNativeOptionIndex(index)].disabled) {
							newIndex = index;
							break;
						}
					}
				}

				this.activeIndex = this.getNativeOptionIndex(newIndex);

				if (this.panelOpen) {
					this.setActiveValue(this.getOptionIndex(this.activeIndex));
				} else {
					this.setSelectedValue(true);
				}
				break;
			case 'Escape':
				event.preventDefault();

				this.close();
				break;
			case 'Space':
			case 'Enter':
				if (this.panelOpen && this.searching && event.code === 'Space') {
					return;
				} else {
					event.preventDefault();
				}

				if (!this.panelOpen) {
					this.open();
				} else {
					this.setSelectedValue(true);

					if (!this.multiple) {
						this.close();
					}
				}
				break;
		}
	}

	private setSelectedValue(shouldForwardEvent: boolean) {
		if (this.activeIndex < 0) {
			return;
		}

		const option = this.element.options[this.activeIndex];

		const currentValue =
			this.select.dataset['value']
				// split(',') on an empty string ('') will give an array of [''] so we need to remove empty strings from the array
				?.split(',')
				?.filter(o => !!o) || [];

		const update = (values: string[]) => {
			const value = values.join(',');
			this.select.dataset['value'] = value;

			this.element.value = values[0]; // select always stores one value (also when multiple!)
			this.element.selectedIndex = this.activeIndex;

			for (const optionElement of Array.from(this.element.options)) {
				optionElement.selected = values.includes(optionElement.value);

				if (optionElement.selected) {
					optionElement.setAttribute('selected', 'selected');
				} else {
					optionElement.removeAttribute('selected');
				}
			}

			this.setTriggerText();
			this.setActiveValue(this.getOptionIndex(this.activeIndex));

			if (value) {
				this.select.classList.add(this.options.activeClassName);
			} else {
				this.select.classList.remove(this.options.activeClassName);
			}

			if (shouldForwardEvent) {
				this.element.dispatchEvent(new Event('change'));
			}
		};

		if (currentValue.includes(option.value)) {
			if (this.multiple) {
				update(currentValue.filter(o => o !== option.value));
			} else {
				// keep same value
				return;
			}
		} else if (!currentValue.includes(option.value)) {
			if (this.multiple) {
				currentValue.push(option.value);

				update([...new Set(currentValue)]);
			} else {
				update([option.value]);
			}
		}
	}

	private setTriggerText() {
		this.triggerTextElement.textContent = this.selectedOptions.map(o => o.textContent).join(', ');
	}

	private setActiveValue(index: number) {
		if (this.overlayRef) {
			const element = this.optionElements[index];

			if (index === 0 || this.optionElements.length === 1 || !element) {
				this.overlayRef.portalElement.scrollTop = 0;
			} else {
				this.overlayRef.portalElement.scrollTop = this.getOptionScrollPosition(
					element.offsetTop,
					element.offsetHeight,
					this.overlayRef.portalElement.scrollTop,
					this.overlayRef.portalElement.offsetHeight
				);
			}
		}

		this.optionElements?.forEach((option, optionIndex) => {
			const value = option.dataset['value'];
			const selected = !!this.selectedOptions.find(o => o.value === value);
			option.classList.toggle(this.options.selectedClassName, selected);
			option.classList.toggle(this.options.activeClassName, optionIndex === index);
		});
	}

	private getNativeOptionIndex(index: number) {
		const option = this.optionElements[index];

		return !option ? -1 : Array.from(this.element.options).findIndex(o => o.value === option.dataset['value']);
	}

	private getOptionIndex(index: number) {
		const option = this.element.options[index];

		return !option ? -1 : this.optionElements.findIndex(o => o.dataset['value'] === option.value);
	}

	private getOptionScrollPosition(optionOffset: number, optionHeight: number, currentScrollPosition: number, panelHeight: number): number {
		if (optionOffset < currentScrollPosition) {
			return optionOffset;
		}

		if (optionOffset + optionHeight > currentScrollPosition + panelHeight) {
			return Math.max(0, optionOffset - panelHeight + optionHeight);
		}

		return currentScrollPosition;
	}
}
